@use 'variables' as *;
@use 'mixins' as *;
@use 'src/components/cat-form-hint/cat-form-hint';
@use 'utils/color';
@use '_snippets/form-label';

:host {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  line-height: 1.25rem;
  position: relative;
}

:host([hidden]) {
  display: none;
}

label {
  align-self: flex-start;

  &.hidden {
    @include cat-visually-hidden;
  }
}

.select-field,
.select-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1 1 auto;
}

.select-field.select-horizontal {
  flex-direction: row;
  gap: 1rem;
}

.select-wrapper {
  display: flex;
  align-items: flex-start;
  background: cat-token('color.ui.background.input');
  border-radius: cat-border-radius('m');
  box-shadow: inset 0 0 0 1px rgb(var(--border-color));
  transition: box-shadow cat-token('time.transition.s') linear;
  padding: 0.25rem;
  --border-color: #{cat-token('color.ui.border.dark', $wrap: false)};

  &:not(.select-disabled):hover {
    box-shadow:
      inset 0 0 0 1px rgb(var(--border-color)),
      0 0 0 1px rgb(var(--border-color));
  }

  &:focus-within {
    outline: 2px solid cat-token('color.ui.border.focus');
    outline-offset: -1px;
  }

  &.select-invalid {
    --border-color: #{cat-token('color.theme.danger.bg', 0.2, $wrap: false)};
  }

  /* stylelint-disable property-no-vendor-prefix */
  &:has(input:-webkit-autofill) {
    &,
    &:hover,
    &:focus {
      background-color: cat-token('color.ui.background.inputAutofill');
    }
  }
  /* stylelint-enable property-no-vendor-prefix */
}

:host(.cat-error) {
  .select-wrapper {
    box-shadow: 0 0 0 1px cat-token('color.theme.danger.bg');

    &:not(.input-disabled):hover {
      box-shadow: 0 0 0 2px cat-token('color.theme.danger.bg');
    }
  }
}

.select-disabled {
  background: cat-token('color.ui.background.muted');
  cursor: not-allowed;
  color: cat-token('color.ui.font.muted');
  pointer-events: none;
}

.select-wrapper-inner {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;

  > cat-avatar {
    display: inline-block;
    padding-left: 0.5rem;

    & + .select-input {
      padding-left: 0.25rem;
    }
  }

  cat-avatar {
    --cat-avatar-size: 1.25rem;
    --cat-avatar-font-size: 0.5rem;
  }

  .select-multiple & {
    flex-wrap: wrap;
  }
}

.select-input {
  font: inherit;
  background: none;
  border: none;
  outline: none;
  padding: 0.375rem 0.5rem;
  flex: 1 1 auto;
  @include cat-ellipsis;

  &::placeholder {
    color: cat-token('color.ui.font.muted');
    opacity: 1;
  }

  .select-disabled & {
    cursor: inherit;
  }
}

.select-pills {
  display: contents;
}

.pill {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.5rem;
  background: cat-token('color.ui.background.muted');
  border-radius: cat-border-radius('s');
  white-space: nowrap;
  min-width: 0;

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1 1 0%;
  }

  > cat-button {
    margin-right: -0.25rem;
    margin-left: -0.25rem;
  }
}

.select-btn {
  transition: transform cat-token('time.transition.s') linear;

  &::part(button) {
    outline: none;
  }
}

cat-spinner {
  padding: 0.375rem;
}

.icon-suffix {
  padding: 0.25rem;
}

.select-btn-open {
  transform: rotate(180deg);
}

.select-dropdown {
  position: fixed;
  right: 0;
  background: cat-token('color.ui.background.surface');
  display: none;
  overflow: auto;
  // -webkit-overflow-scrolling: touch;
  @include cat-elevation(4);
  border-radius: cat-border-radius('m');
  border: 1px solid cat-token('color.ui.border.default');
  z-index: cat-z-index('dropdown');

  /* Avoid layout interference */
  width: max-content;
  top: 0;
  left: 0;
}

.select-options-wrapper {
  max-height: 16rem;
  width: 100%;
}

.select-empty {
  margin: 1rem 0;
  padding: 0 1.25rem;
}

.select-options {
  list-style-type: none;
  margin: 0;
  padding: 0.5rem 0;

  cat-checkbox,
  .select-option-single {
    margin: 0;
    padding: 0.5rem 1rem;
  }
}

.select-option-inner {
  display: flex;
  gap: 0.5rem;

  cat-avatar {
    --cat-avatar-size: 1.25rem;
    --cat-avatar-font-size: 0.5rem;
  }
}

.select-option-text {
  flex: 1 1 0%;
  min-width: 0;
}

.select-option-single {
  cursor: pointer;
}

.select-input-transparent-caret {
  caret-color: transparent;
}

.select-option-empty,
.select-option-loading {
  padding: 0.5rem 1rem;
}

.select-option:hover {
  background-color: cat-token('color.theme.secondary.bg', 0.05);
}

.select-option-active {
  outline: 2px solid cat-token('color.ui.border.focus');
  outline-offset: -2px;
}

.select-option-label {
  @include cat-ellipsis(2);
  @include cat-break-word;
}

.select-option-description {
  @include cat-ellipsis(2);
  color: cat-token('color.ui.font.muted');
}
